import React, { ReactElement } from 'react';
import PropTypes from 'prop-types';
import styled, { CSSObject } from '@emotion/styled';
import { getBackgroundTheme, getPaymentTheme, getTextColor } from '../../style';
import { addCommas, isDarkMode } from '../../utils/helpers';
import AutoTopupToggle from './AutoTopup/AutoTopupToggle';
import PromoCode from './PromoCode/PromoCode';
import { getPackageSubTotal, getPackageVat } from '../../utils/billing';
import { IOrderSummaryProps } from './types/OrderSummary';
import breakpoints from '../../theme/material/breakpoints';

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const OrderSummaryContent = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  border: isDarkMode() ? 'none' : `1px solid ${getPaymentTheme().SUMMARY.borderColor}`,
  borderRadius: 6,
}));

const OrderSection = styled('div')(() => ({
  padding: '10px 0',
  width: '100%',
  color: isDarkMode() ? '#F6F4F4' : '#353946',
  fontSize: 15,
  letterSpacing: -0.3,
  lineHeight: '20px',
}));

const ItemChoice = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 6,
  fontSize: 18,
  color: isDarkMode() ? '#F6F4F4' : '#212121',
  fontWeight: 600,
  lineHeight: '21px',
  letterSpacing: -0.18,
  [breakpoints.down(769.05)]: {
    letterSpacing: -0.3,
  },
}));

const VatDetails = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: 16,
  ...getTextColor().LIGHT_GRAY,
  fontWeight: 400,
}));

const CreditText = styled('span')(() => ({
  color: isDarkMode() ? '#F6F4F4' : '#212121',
  fontWeight: 900,
}));

const SummaryAmountText = styled('div')(() => ({
  ...getTextColor().LIGHT_GRAY,
  fontWeight: 400,
  fontSize: 16,
  letterSpacing: -0.3,
  lineHeight: '21px',
  marginLeft: 'auto',
  [breakpoints.down(769.05)]: {
    fontSize: 15,
    lineHeight: '20px',
  },
}));

const Total = styled('div')(
  (): CSSObject => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 18,
    lineHeight: '23px',
    letterSpacing: -0.3,
    textTransform: 'uppercase',
    color: isDarkMode() ? '#FFF' : '#212121',
    fontWeight: 700,
    backgroundColor: getBackgroundTheme().paymentHighlight,
    padding: 15,
    borderRadius: '0px 0px 6px 6px',
    [breakpoints.down(769.05)]: {
      padding: '15px 12px',
    },
  })
);

const TermsCond = styled('span')(() => ({
  color: getTextColor().DARK.color,
  fontSize: 14,
  fontWeight: 400,
  [breakpoints.down(769.05)]: {
    fontSize: 13,
  },
}));

const OrderSummaryDivider = styled('hr')(() => ({
  border: 'none',
  borderTop: `1px dashed ${getPaymentTheme().DIVIDER.borderColor}`,
  margin: '15px 0',
}));

const OrderSummaryItems = styled('div')(() => ({
  ...(isDarkMode()
    ? {
        border: `1px solid ${getPaymentTheme().SUMMARY_SECONDARY.borderColor}`,
        borderRadius: '6px 6px 0px 0px',
        borderBottom: 'none',
      }
    : {}),
  padding: 15,
  [breakpoints.down(769.05)]: {
    padding: '15px 12px',
  },
}));

const TermsCondWrap = styled('div')({
  textAlign: 'center',
  padding: 10,
});

const OrderSummary = ({
  price,
  credits,
  currencyCharCode,
  currencyCode,
  vatDetails,
  isIDV,
  discountAmount,
  recurring,
  onAutoTopupChange,
  selectedPackageData,
  packages,
  discountData,
  profile,
  setDiscount,
  setDiscountAmount,
  applyPromoCode,
  setSelectedPackageData,
  taxCountry,
  paymentChannel = null,
}: IOrderSummaryProps): ReactElement => {
  const discount = discountAmount === undefined ? 0 : discountAmount;
  const grandTotal = getPackageSubTotal(selectedPackageData, discount, vatDetails);

  return isIDV ? (
    <div>
      <OrderSection>Your Order Details</OrderSection>
      <OrderSummaryContent>
        <OrderSummaryItems>
          <ItemChoice>
            <b>Verification Package</b>
            <div>
              {String.fromCharCode(currencyCharCode)}
              {parseFloat(price).toFixed(2)} {currencyCode}
            </div>
          </ItemChoice>
          <OrderSummaryDivider />
          <PromoCode
            discountAmount={discountAmount}
            currencyCharCode={currencyCharCode}
            currencyCode={currencyCode}
            discount={discountData}
            profile={profile}
            setDiscount={setDiscount}
            setDiscountAmount={setDiscountAmount}
            selectedPackage={selectedPackageData}
            applyPromoCode={applyPromoCode}
            paymentChannel={paymentChannel}
            setSelectedPackageData={setSelectedPackageData}
            taxCountry={taxCountry}
          />
          {vatDetails && (
            <div>
              <OrderSummaryDivider />
              <VatDetails>
                <div>Estimated tax ({`${vatDetails.location} ${vatDetails.rate * 100}%`})</div>
                <SummaryAmountText>
                  {String.fromCharCode(currencyCharCode)}
                  {getPackageVat(selectedPackageData, discount, vatDetails).toFixed(2)} {currencyCode}
                </SummaryAmountText>
              </VatDetails>
            </div>
          )}
        </OrderSummaryItems>
        <Total>
          <div>TOTAL:</div>
          <b>
            {String.fromCharCode(currencyCharCode)}
            {grandTotal.toFixed(2)} {currencyCode}
          </b>
        </Total>
      </OrderSummaryContent>
      <TermsCondWrap>
        <TermsCond>Identity verification offered by a third-party service</TermsCond>.
      </TermsCondWrap>
    </div>
  ) : (
    <div>
      <OrderSection>Order Details</OrderSection>

      <OrderSummaryContent>
        <OrderSummaryItems>
          <ItemChoice>
            <CreditText>
              {
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                addCommas(credits)
              }
            </CreditText>{' '}
            Credit Package
            <SummaryAmountText>
              {String.fromCharCode(currencyCharCode)}
              {parseFloat(price).toFixed(2)} {currencyCode}
            </SummaryAmountText>
          </ItemChoice>
          <OrderSummaryDivider />
          <AutoTopupToggle
            recurring={recurring}
            onAutoTopupChange={onAutoTopupChange}
            packages={packages}
            selectedPackage={selectedPackageData}
          />

          <OrderSummaryDivider />
          <PromoCode
            discountAmount={discountAmount}
            currencyCharCode={currencyCharCode}
            currencyCode={currencyCode}
            discount={discountData}
            profile={profile}
            setDiscount={setDiscount}
            setDiscountAmount={setDiscountAmount}
            selectedPackage={selectedPackageData}
            applyPromoCode={applyPromoCode}
            setSelectedPackageData={setSelectedPackageData}
            taxCountry={taxCountry}
            paymentChannel={paymentChannel}
          />

          {vatDetails && (
            <div>
              <OrderSummaryDivider />
              <VatDetails>
                <div>Estimated tax ({`${vatDetails.location} ${vatDetails.rate * 100}%`})</div>
                <SummaryAmountText>
                  {String.fromCharCode(currencyCharCode)}
                  {getPackageVat(selectedPackageData, discount, vatDetails).toFixed(2)} {currencyCode}
                </SummaryAmountText>
              </VatDetails>
            </div>
          )}
        </OrderSummaryItems>
        <Total>
          <div>TOTAL:</div>
          <strong>
            {String.fromCharCode(currencyCharCode)}
            {grandTotal.toFixed(2)} {currencyCode}
          </strong>
        </Total>
      </OrderSummaryContent>
    </div>
  );
};

OrderSummary.propTypes = {
  price: PropTypes.string,
  credits: PropTypes.number,
  currencyCharCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currencyCode: PropTypes.string,
  vatDetails: PropTypes.shape({
    rate: PropTypes.number,
    tax_amount: PropTypes.number,
    gross_amount: PropTypes.number,
  }),
  hasRecurring: PropTypes.bool,
};

export default OrderSummary;
